import { useState, useRef, FormEvent, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FilePlus2,
  Upload,
  X,
  Loader2,
  CheckCircle,
  FileText,
} from 'lucide-react';
import {
  createCase,
  updateCase,
  uploadDocuments,
  type CaseFact,
} from '../api/cases';
import { MANUAL_CASE_LABELS } from '../config/labels';
import { useToastStore } from '../stores/toast';

interface FormData {
  issuer_name: string;
  ticker: string;
  cusip: string;
  depositor_name: string;
  depositor_type: string;
  account_ref: string;
  deposit_date: string;
  share_quantity: string;
  notes: string;
}

const EMPTY_FORM: FormData = {
  issuer_name: '',
  ticker: '',
  cusip: '',
  depositor_name: '',
  depositor_type: '',
  account_ref: '',
  deposit_date: '',
  share_quantity: '',
  notes: '',
};

function fieldToFacts(form: FormData): CaseFact[] {
  const facts: CaseFact[] = [];
  const entries: [string, string][] = [
    ['issuer_name', form.issuer_name],
    ['issuer_symbol', form.ticker],
    ['cusip', form.cusip],
    ['depositor_name', form.depositor_name],
    ['depositor_type', form.depositor_type],
    ['account_ref', form.account_ref],
    ['deposit_date', form.deposit_date],
    ['share_quantity', form.share_quantity],
  ];
  for (const [name, value] of entries) {
    if (value.trim()) {
      facts.push({
        field_name: name,
        field_value: value.trim(),
        provenance: { source: 'manual' },
        is_verifiable: true,
        source_type: 'manual',
      });
    }
  }
  return facts;
}

export default function CaseManual() {
  const navigate = useNavigate();
  const addToast = useToastStore((s) => s.addToast);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const setField = (key: keyof FormData, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const { [key]: _, ...rest } = e;
      return rest;
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.issuer_name.trim()) errs.issuer_name = 'Issuer name is required';
    if (!form.depositor_name.trim()) errs.depositor_name = 'Depositor name is required';
    if (!form.deposit_date.trim()) errs.deposit_date = 'Deposit date is required';
    if (form.share_quantity && isNaN(Number(form.share_quantity))) {
      errs.share_quantity = 'Must be a number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return;
    setFiles((prev) => [...prev, ...Array.from(newFiles)]);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitStatus('Creating case...');
    try {
      // Create the case with ALL form fields as case model fields
      // (not just notes). Map frontend field names to backend names.
      const newCase = await createCase({
        issuer_name: form.issuer_name || undefined,
        issuer_symbol: form.ticker || undefined,
        cusip: form.cusip || undefined,
        depositor_name: form.depositor_name || undefined,
        depositor_type: form.depositor_type || undefined,
        account_ref: form.account_ref || undefined,
        deposit_date: form.deposit_date || undefined,
        share_quantity: form.share_quantity ? Number(form.share_quantity) : undefined,
        notes: form.notes || undefined,
      });
      // Also store as CaseFact records for provenance tracking.
      const facts = fieldToFacts(form);
      if (facts.length > 0) {
        setSubmitStatus('Saving case facts...');
        await updateCase(newCase.id, { facts });
      }
      if (files.length > 0) {
        setSubmitStatus(`Uploading ${files.length} document${files.length > 1 ? 's' : ''}...`);
        try {
          await uploadDocuments(newCase.id, files);
        } catch {
          addToast(MANUAL_CASE_LABELS.docUploadError, 'error');
        }
      }
      addToast(MANUAL_CASE_LABELS.caseCreated, 'success');
      navigate(`/cases/${newCase.id}`);
    } catch {
      addToast('Failed to create case', 'error');
    } finally {
      setSubmitting(false);
      setSubmitStatus('');
    }
  };

  const inputClass = (field: keyof FormData): string =>
    `input ${errors[field] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <FilePlus2 className="text-primary-600" size={24} />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {MANUAL_CASE_LABELS.title}
          </h1>
          <p className="text-sm text-slate-500">
            {MANUAL_CASE_LABELS.subtitle}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Case Details */}
        <div className="card p-6 animate-fade-in">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Case Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.issuerName} *
              </label>
              <input
                type="text"
                className={inputClass('issuer_name')}
                placeholder={MANUAL_CASE_LABELS.issuerNamePlaceholder}
                value={form.issuer_name}
                onChange={(e) => setField('issuer_name', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.issuer_name}
              />
              {errors.issuer_name && (
                <p className="text-xs text-red-600 mt-1">{errors.issuer_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.ticker}
              </label>
              <input
                type="text"
                className={inputClass('ticker')}
                placeholder={MANUAL_CASE_LABELS.tickerPlaceholder}
                value={form.ticker}
                onChange={(e) => setField('ticker', e.target.value.toUpperCase())}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.cusip}
              </label>
              <input
                type="text"
                className={inputClass('cusip')}
                placeholder={MANUAL_CASE_LABELS.cusipPlaceholder}
                value={form.cusip}
                onChange={(e) => setField('cusip', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.depositorName} *
              </label>
              <input
                type="text"
                className={inputClass('depositor_name')}
                placeholder={MANUAL_CASE_LABELS.depositorNamePlaceholder}
                value={form.depositor_name}
                onChange={(e) => setField('depositor_name', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.depositor_name}
              />
              {errors.depositor_name && (
                <p className="text-xs text-red-600 mt-1">{errors.depositor_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.depositorType}
              </label>
              <select
                className={inputClass('depositor_type')}
                value={form.depositor_type}
                onChange={(e) => setField('depositor_type', e.target.value)}
              >
                <option value="">Select type...</option>
                {MANUAL_CASE_LABELS.depositorTypeOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.accountRef}
              </label>
              <input
                type="text"
                className={inputClass('account_ref')}
                placeholder={MANUAL_CASE_LABELS.accountRefPlaceholder}
                value={form.account_ref}
                onChange={(e) => setField('account_ref', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.depositDate} *
              </label>
              <input
                type="date"
                className={inputClass('deposit_date')}
                value={form.deposit_date}
                onChange={(e) => setField('deposit_date', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.deposit_date}
              />
              {errors.deposit_date && (
                <p className="text-xs text-red-600 mt-1">{errors.deposit_date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.shareQuantity}
              </label>
              <input
                type="number"
                className={inputClass('share_quantity')}
                placeholder={MANUAL_CASE_LABELS.shareQuantityPlaceholder}
                value={form.share_quantity}
                onChange={(e) => setField('share_quantity', e.target.value)}
                aria-invalid={!!errors.share_quantity}
              />
              {errors.share_quantity && (
                <p className="text-xs text-red-600 mt-1">{errors.share_quantity}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                {MANUAL_CASE_LABELS.notes}
              </label>
              <textarea
                className={inputClass('notes')}
                placeholder={MANUAL_CASE_LABELS.notesPlaceholder}
                rows={3}
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Document Upload */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {MANUAL_CASE_LABELS.documents}
          </h2>
          <div
            className={`dropzone ${dragOver ? 'dropzone-active' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="Upload documents"
          >
            <Upload className="mx-auto text-slate-400 mb-2" size={32} />
            <p className="text-sm text-slate-600 font-medium">
              {MANUAL_CASE_LABELS.dropFilesHere}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {files.length > 0 && (
            <ul className="mt-3 space-y-2">
              {files.map((file, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <FileText size={16} className="text-slate-400 shrink-0" />
                  <span className="text-sm text-slate-700 flex-1 truncate">
                    {file.name}
                  </span>
                  <span className="text-xs text-slate-400">
                    {(file.size / 1024).toFixed(0)} KB
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    aria-label={`Remove ${file.name}`}
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/cases')}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
          >
            {submitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {submitStatus || MANUAL_CASE_LABELS.creating}
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                {MANUAL_CASE_LABELS.createCase}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}